import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatCurrency, formatDate, formatApiError, computeFees, posterSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import SeatMap from "../components/SeatMap";
import FollowButton from "../components/FollowButton";
import { Calendar, MapPin, Minus, Plus, ShieldCheck, Users, Armchair } from "lucide-react";

export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [tierIdx, setTierIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [seats, setSeats] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [provider, setProvider] = useState("stripe");
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setProvider(data.provider)).catch(() => {});
  }, []);
  useEffect(() => {
    if (event && user) api.get(`/organizers/${event.organizer_id}`).then(({ data }) => setFollowing(data.following)).catch(() => {});
  }, [event, user]);

  useEffect(() => {
    api.get(`/events/${id}`).then(({ data }) => setEvent(data)).catch(() => navigate("/events"));
    if (params.get("cancelled")) toast("Payment cancelled — your seats were released.");
  }, [id, navigate, params]);

  if (!event) {
    return <div className="flex min-h-[50vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" /></div>;
  }

  const tier = event.tiers[tierIdx];
  const cancelled = event.status === "cancelled";
  const available = cancelled ? 0 : tier.quantity - (tier.sold || 0);
  const count = tier.seated ? seats.length : qty;
  const fees = computeFees(tier.price, count);

  const toggleSeat = (label) => setSeats((s) => (s.includes(label) ? s.filter((x) => x !== label) : s.length >= 10 ? s : [...s, label]));

  const pay = async () => {
    if (!user) { navigate("/login", { state: { from: `/events/${id}` } }); return; }
    if (tier.seated && seats.length === 0) { toast.error("Pick your seats first"); return; }
    if (!tier.seated && qty > available) { toast.error(`Only ${available} tickets available`); return; }
    setProcessing(true);
    const payload = { event_id: event.id, tier_name: tier.name, quantity: count, seats, origin_url: window.location.origin };
    try {
      if (provider === "razorpay") {
        const { data } = await api.post("/payments/razorpay/order", payload);
        await new Promise((res, rej) => { if (window.Razorpay) return res(); const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js"; s.onload = res; s.onerror = rej; document.body.appendChild(s); });
        const rz = new window.Razorpay({
          key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id, name: "Afterhours", description: `${event.title} · ${tier.name}`,
          prefill: data.prefill, theme: { color: "#C94A6A" },
          handler: async (resp) => {
            try { const v = await api.post("/payments/razorpay/verify", resp); navigate(v.data.payment_status === "paid" ? `/bookings/${v.data.ref_id}` : "/my-bookings"); }
            catch (e) { toast.error(formatApiError(e)); setProcessing(false); }
          },
          modal: { ondismiss: () => setProcessing(false) },
        });
        rz.open();
        return;
      }
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setProcessing(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      {cancelled && <div data-testid="event-cancelled-banner" className="mb-6 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200"><span className="font-bold">This event was cancelled by the organizer.</span> All ticket holders are being refunded in full (tickets + fees).</div>}
      <div className="relative mb-8 overflow-hidden rounded-3xl border border-zinc-800/80">
        <img src={posterSrc(event.poster_url)} alt={event.title} className="h-[380px] w-full object-cover sm:h-[500px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2B0B15] via-[#2B0B15]/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
          <div className="mb-3 flex gap-2">
            <span className="rounded-full border border-amber-500/40 bg-[#2B0B15]/80 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-amber-400 backdrop-blur">{event.category}</span>
            {event.featured_until && new Date(event.featured_until) > new Date() && <span className="rounded-full bg-punch-500 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">Spotlight</span>}
          </div>
          <h1 className="font-display text-3xl font-extrabold leading-tight text-zinc-50 sm:text-5xl">{event.title}</h1>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-zinc-300">
            <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-amber-500" /><span className="font-mono">{formatDate(event.date)}</span></div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-500" /><span>{event.venue}, {event.city}</span></div>
            <div className="flex items-center gap-2"><Users className="h-4 w-4 text-amber-500" /><Link data-testid="organizer-link" to={`/organizers/${event.organizer_id}`} className="hover:text-blush">By {event.organizer_name}</Link><ShieldCheck className="h-3.5 w-3.5 text-amber-400" title="PAN verified" /><FollowButton organizerId={event.organizer_id} initialFollowing={following} compact /></div>
          </div>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="mb-3 font-display text-xl font-bold text-zinc-50">About this event</h2>
          <p className="whitespace-pre-line leading-relaxed text-zinc-300">{event.description}</p>

          <h2 className="mb-3 mt-10 font-display text-xl font-bold text-zinc-50">Ticket tiers</h2>
          <div className="space-y-3">
            {event.tiers.map((t, i) => {
              const left = t.quantity - (t.sold || 0);
              const active = tierIdx === i;
              return (
                <button key={t.name} data-testid={`tier-select-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => { setTierIdx(i); setQty(1); setSeats([]); }}
                  className={`w-full rounded-2xl border p-4 text-left transition ${active ? "border-amber-500 bg-amber-500/5" : "border-zinc-800 bg-[#3A1020] hover:border-amber-500/40"}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 font-display text-lg font-bold text-zinc-50">{t.name}{t.seated && <Armchair className="h-4 w-4 text-punch-400" />}</div>
                      <div className="mt-1 font-mono text-xs uppercase tracking-widest text-zinc-500">{left > 0 ? (t.seated ? "Reserved seating" : "Available") : "Sold out"}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-xl font-bold text-amber-400">{formatCurrency(t.price)}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">+ 5% fee & GST</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {tier.seated && (
            <div className="mt-8">
              <h2 className="mb-3 font-display text-xl font-bold text-zinc-50">Pick your seats <span className="text-sm font-normal text-zinc-500">(max 10)</span></h2>
              <SeatMap tier={tier} selected={seats} onToggle={toggleSeat} />
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-24 lg:h-fit">
          <div className="rounded-3xl border border-amber-500/20 bg-[#3A1020] p-6 glow-amber">
            <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-amber-400">You're booking</div>
            <div className="font-display text-lg font-bold text-zinc-50">{tier.name}</div>
            <div className="mt-1 font-mono text-2xl font-bold text-amber-400">{formatCurrency(tier.price)}</div>

            {tier.seated ? (
              <div className="mt-6">
                <div className="mb-2 font-mono text-xs uppercase tracking-widest text-zinc-500">Seats</div>
                <div data-testid="selected-seats" className="flex min-h-10 flex-wrap gap-1.5 rounded-lg border border-zinc-800 bg-[#33101C] p-2">
                  {seats.length === 0 ? <span className="text-xs text-zinc-500">Tap seats on the map</span> : seats.map((s) => <span key={s} className="rounded bg-amber-500 px-2 py-0.5 font-mono text-xs font-bold text-zinc-950">{s}</span>)}
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <div className="mb-2 flex items-center justify-between text-xs">
                  <span className="font-mono uppercase tracking-widest text-zinc-500">Quantity</span>
                  <span className="text-zinc-500">max 20</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button data-testid="qty-minus-btn" variant="outline" size="icon" onClick={() => setQty(Math.max(1, qty - 1))} className="h-10 w-10 border-zinc-700 bg-[#2F0D18] text-zinc-100 hover:bg-zinc-900 hover:text-amber-400"><Minus className="h-4 w-4" /></Button>
                  <div data-testid="qty-display" className="flex-1 rounded-lg border border-zinc-800 bg-[#2F0D18] py-2 text-center font-mono text-lg font-bold text-zinc-50">{qty}</div>
                  <Button data-testid="qty-plus-btn" variant="outline" size="icon" onClick={() => setQty(Math.min(Math.max(Math.min(available, 20), 1), qty + 1))} className="h-10 w-10 border-zinc-700 bg-[#2F0D18] text-zinc-100 hover:bg-zinc-900 hover:text-amber-400"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            )}

            <div className="mt-6 space-y-2 border-t border-zinc-800 pt-4 text-sm">
              <div className="flex justify-between text-zinc-400"><span>Tickets × {count}</span><span className="font-mono text-zinc-200">{formatCurrency(fees.subtotal)}</span></div>
              <div className="flex justify-between text-zinc-400"><span>Booking fee (5%)</span><span className="font-mono text-zinc-200">{formatCurrency(fees.commission)}</span></div>
              <div className="flex justify-between text-zinc-400"><span>GST on fee (18%)</span><span className="font-mono text-zinc-200">{formatCurrency(fees.gst)}</span></div>
              <div className="flex justify-between border-t border-zinc-800 pt-3 text-base">
                <span className="font-semibold text-zinc-100">Total</span>
                <span data-testid="checkout-total" className="font-mono text-lg font-bold text-amber-400">{formatCurrency(fees.total)}</span>
              </div>
            </div>

            <Button data-testid="book-now-btn" onClick={pay} disabled={available === 0 || processing} className="mt-5 h-12 w-full bg-amber-500 font-bold uppercase tracking-wider text-zinc-950 hover:bg-amber-400 disabled:opacity-40">
              {available === 0 ? (cancelled ? "Event cancelled" : "Sold out") : processing ? "Redirecting to payment..." : `Pay ${formatCurrency(fees.total)}`}
            </Button>
            <div className="mt-3 flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <ShieldCheck className="h-3 w-3 text-amber-500" /> {provider === "razorpay" ? "UPI · Cards · Netbanking via Razorpay" : "Secured by Stripe"} · QR ticket emailed instantly
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
